import { FeatureFlag } from '@ps/web-core-enums';
import { usePrevious } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEqual } from 'lodash';
import React, { createRef, useCallback, useEffect, useMemo, useState } from 'react';

import useFeature from '@swf/core/hooks/useFeature';

import styles from './RouletteStatus.scss';

export type ValueType = string | Record<string, string>;

interface Props {
  columns?: number;
  count: number;
  initial: string[];
  className?: string;
}

interface TokenProps {
  value: ValueType;
  className: string;
  key: number;
}

const cleanupTime = 2000;
const maximumNumbers = 7;
let key = 0;

const colors = (() => {
  const red = [32, 19, 21, 25, 34, 27, 36, 30, 23, 5, 16, 1, 14, 9, 18, 7, 12, 3];
  const black = [15, 4, 2, 17, 6, 13, 11, 8, 10, 24, 33, 20, 31, 22, 29, 28, 35, 26];
  const result = {} as Record<number, string>;
  for (let i = 0; i < red.length; i++) {
    result[red[i]] = styles.red;
    result[black[i]] = styles.black;
  }
  return result;
})();

const classForNumber = (value: string) => {
  const asInt = parseInt(value);
  return asInt === 0 ? styles.green : colors[parseInt(value)];
};

const textValue = (value: ValueType) => {
  if (value.length || typeof value == 'number') {
    return value as string;
  }
  return Object.keys(value)[0];
};
const numberMultiplier = (value: ValueType) =>
  value?.length ? undefined : Object.values(value)[0];

const createToken = (testValue: ValueType): TokenProps => {
  const decoded = testValue.split ? (testValue as string).split('%') : false;
  const value =
    Array.isArray(decoded) && decoded.length > 1
      ? ({ [decoded[0]]: decoded[1] } as ValueType)
      : testValue;
  return {
    value,
    key: ++key,
    className: classForNumber(textValue(value))
  };
};

const valueMultiplier = (multiplier?: string) =>
  multiplier && <div className={styles.valueMultiplier}>x{multiplier}</div>;

const RouletteStatus: React.FC<Props> = ({ initial, className }) => {
  const container = createRef<HTMLDivElement>();
  const holder = createRef<HTMLDivElement>();
  const floater = createRef<HTMLDivElement>();

  const appendList: ValueType[] = useMemo(() => [], []);
  const [injected, setInjected] = useState<boolean>(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [renderList, setRenderList] = useState<TokenProps[]>([]);

  const [floaterValue, setFloaterValue] = useState<TokenProps>();
  const [floaterClass, setFloaterClass] = useState<string>();
  const oldInitial = usePrevious(initial);

  const featureEnabled = useFeature(FeatureFlag.CasinoLiveRoulette);

  useEffect(() => {
    if (initialLoad) {
      const oldChunk = initial.slice(-maximumNumbers);
      const remainder = initial.slice(0, initial.length - oldChunk.length).reverse();

      setRenderList(oldChunk.map((value) => createToken(value)));
      remainder.forEach((v) => insertValue(v));

      setInitialLoad(false);
    } else if (!initialLoad && oldInitial.length && !isEqual(oldInitial, initial)) {
      insertValue(initial[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const pageInject = useCallback(
    (value: ValueType) => {
      setInjected(true);
      const newToken = createToken(value);
      const newList = [createToken(value), ...renderList.splice(0, maximumNumbers)];
      setRenderList(newList);
      setFloaterValue(newToken);
      setFloaterClass(classForNumber(newToken.value as string));
      setTimeout(() => setInjected(false), cleanupTime);
    },
    [renderList]
  );

  const insertValue = useCallback(
    (update: ValueType) => {
      if (container.current && !injected && !appendList.length && !initialLoad) {
        pageInject(update);
      } else {
        appendList.push(update);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appendList, injected, container, pageInject]
  );

  useEffect(() => {
    let canRun = true;
    let animationId = 0;
    let timeoutClear: NodeJS.Timeout;
    if (!injected && appendList.length) {
      animationId = window.requestAnimationFrame(() => {
        if (!renderList.length || !canRun) return;
        timeoutClear = setTimeout(() => {
          pageInject(appendList.shift() as ValueType);
        }, 5);
      });
    }
    return () => {
      if (animationId) window.cancelAnimationFrame(animationId);
      if (timeoutClear !== undefined) clearTimeout(timeoutClear);
      canRun = false;
    };
  }, [injected, appendList, pageInject, renderList]);

  const existing = renderList?.map((token) => (
    <div key={token.key} className={classNames(styles.ele, token.className)}>
      {textValue(token.value)} {valueMultiplier(numberMultiplier(token.value))}
    </div>
  ));

  return featureEnabled ? (
    <div
      className={classNames(styles.container, className, { [styles.injected]: injected })}
      ref={container}
    >
      <div className={styles.holder} ref={holder}>
        <div className={classNames(styles.floater, floaterClass)} ref={floater}>
          {floaterValue && textValue(floaterValue.value)}{' '}
          {floaterValue && valueMultiplier(numberMultiplier(floaterValue.value))}
        </div>
        {existing}
      </div>
    </div>
  ) : null;
};

export default RouletteStatus;
